import { useState, type FormEvent } from 'react';
import { KeyRound } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { setPassword } from '../api/candidate';
import { newPasswordProblem } from '../lib/password';
import { Button, TextField } from '../components/charte';

/**
 * Accueil de l'espace élève (J3, 28/10/2026) : l'élève arrivé par le lien de l'e-mail, sans mot de passe, le choisit
 * avant d'entrer. Il peut passer l'étape (« Plus tard ») : elle revient à sa prochaine connexion par lien, et
 * « Définir un mot de passe » reste dans le menu du compte.
 */
export default function WelcomePasswordScreen() {
  const candidate = useAuthStore((s) => s.candidate);
  const setCandidate = useAuthStore((s) => s.setCandidate);
  const skipPasswordStep = useAuthStore((s) => s.skipPasswordStep);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = newPasswordProblem(next, confirm);
    setError(problem);
    if (problem) return;
    setBusy(true);
    try {
      await setPassword(next);
      if (candidate) setCandidate({ ...candidate, hasPassword: true });
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message?: unknown }).message) : '';
      setError(msg || 'Impossible d’enregistrer le mot de passe. Réessayez.');
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-hero-indigo px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src="/logo-permio-eleve.png" alt="Permio" className="h-9 w-auto" />
        </div>
        <div className="rounded-card bg-white p-6 shadow-card sm:p-8">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-surface-indigo text-primary">
              <KeyRound size={24} aria-hidden />
            </div>
            <h1 className="text-title-md text-heading">Bienvenue{candidate?.firstName ? ` ${candidate.firstName}` : ''} !</h1>
            <p className="mt-2 text-sm leading-relaxed text-secondary">
              Choisissez votre mot de passe : vous vous connecterez ensuite à votre espace élève avec votre adresse e-mail et
              ce mot de passe, sans attendre de lien.
            </p>
          </div>
          <form onSubmit={submit} className="grid gap-4">
            {/* Identifiant pour le gestionnaire de mots de passe du navigateur. */}
            {candidate?.email && <input type="email" autoComplete="username" value={candidate.email} readOnly hidden />}
            {candidate?.email && (
              <p className="rounded-xl bg-surface-light px-3.5 py-2.5 text-sm text-secondary">
                Adresse de connexion : <span className="font-medium text-heading">{candidate.email}</span>
              </p>
            )}
            <TextField
              label="Mot de passe"
              hint="8 caractères minimum."
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <TextField
              label="Confirmer le mot de passe"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {error && (
              <p className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" className="h-11 w-full" loading={busy}>
              Enregistrer et accéder à mon espace
            </Button>
            <Button variant="ghost" className="w-full" disabled={busy} onClick={skipPasswordStep}>
              Plus tard
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
